import { useState } from 'react'
import { Eye, BookOpen, UserCog, Loader2, Search, UsersRound, UserMinus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useAdminUsers, AdminUser } from '@/services/admin/users.service'
import { useCompanies } from '@/services/admin/companies.service'
import { useAuth } from '@/hooks/use-auth'
import { isSuperAdmin } from '@/types/auth'
import { EnrollUserDialog } from './EnrollUserDialog'
import { UserDetailsDialog } from './UserDetailsDialog'
import { EditUserDialog } from './EditUserDialog'
import { BulkEnrollDialog } from './BulkEnrollDialog'
import { BulkUnenrollDialog } from './BulkUnenrollDialog'

export function UsersManagement() {
  const { user: authUser } = useAuth()
  const userIsSuperAdmin = isSuperAdmin(authUser?.role)
  const [search, setSearch] = useState('')
  const [companyFilter, setCompanyFilter] = useState<string | undefined>(undefined)
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false)
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [bulkEnrollOpen, setBulkEnrollOpen] = useState(false)
  const [bulkUnenrollOpen, setBulkUnenrollOpen] = useState(false)

  const { data: companies } = useCompanies(userIsSuperAdmin)
  const { data: users, isLoading } = useAdminUsers(search, companyFilter)

  const handleEnroll = (user: AdminUser) => {
    setSelectedUser(user)
    setEnrollDialogOpen(true)
  }

  const handleViewDetails = (user: AdminUser) => {
    setSelectedUser(user)
    setDetailsDialogOpen(true)
  }

  const handleEdit = (user: AdminUser) => {
    setSelectedUser(user)
    setEditDialogOpen(true)
  }

  const filteredUsers = users?.filter((user) => {
    if (!search) return true
    const searchLower = search.toLowerCase()
    return (
      user.name.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower)
    )
  })

  return (
    <>
      <Card className="glass-card border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>Gerenciamento de Usuários</CardTitle>
              <CardDescription>
                Gerencie os usuários da plataforma e suas matrículas
              </CardDescription>
            </div>
            {userIsSuperAdmin && (
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" onClick={() => setBulkUnenrollOpen(true)}>
                  <UserMinus className="mr-2 h-4 w-4" />
                  Descadastro em Massa
                </Button>
                <Button onClick={() => setBulkEnrollOpen(true)}>
                  <UsersRound className="mr-2 h-4 w-4" />
                  Cadastro em Massa
                </Button>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent>
          {/* Filtros */}
          <div className="mb-6 flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Buscar por nome ou email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            {userIsSuperAdmin && (
              <Select
                value={companyFilter ?? 'all'}
                onValueChange={(v) => setCompanyFilter(v === 'all' ? undefined : v)}
              >
                <SelectTrigger className="w-[220px]">
                  <SelectValue placeholder="Todas as empresas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as empresas</SelectItem>
                  <SelectItem value="none">Sem empresa</SelectItem>
                  {companies?.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Tabela */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredUsers && filteredUsers.length > 0 ? (
            <div className="rounded-md border border-border/50">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    {userIsSuperAdmin && <TableHead>Empresa</TableHead>}
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-center">Cursos</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={user.avatar} />
                            <AvatarFallback className="text-xs">
                              {user.name.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span>{user.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{user.email}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            user.role === 'admin' || user.role === 'super_admin'
                              ? 'default'
                              : 'secondary'
                          }
                        >
                          {user.role === 'super_admin'
                            ? 'Super Admin'
                            : user.role === 'admin'
                              ? 'Admin'
                              : 'Usuário'}
                        </Badge>
                      </TableCell>
                      {userIsSuperAdmin && (
                        <TableCell>
                          {user.company ? (
                            <span className="text-sm">{user.company.name}</span>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell className="text-center">
                        <Badge variant={user.active ? 'default' : 'outline'}>
                          {user.active ? '✅ Ativo' : '🚫 Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-primary font-semibold">
                          {user._count.course_enrollments}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewDetails(user)}
                            title="Ver detalhes"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {/* <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEnroll(user)}
                            title="Matricular em curso"
                          >
                            <BookOpen className="w-4 h-4" />
                          </Button> */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(user)}
                            title="Editar status"
                          >
                            <UserCog className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p>Nenhum usuário encontrado.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      {bulkEnrollOpen && (
        <BulkEnrollDialog open={bulkEnrollOpen} onOpenChange={setBulkEnrollOpen} />
      )}

      {bulkUnenrollOpen && (
        <BulkUnenrollDialog open={bulkUnenrollOpen} onOpenChange={setBulkUnenrollOpen} />
      )}
      {selectedUser && (
        <>
          <EnrollUserDialog
            user={selectedUser}
            open={enrollDialogOpen}
            onOpenChange={setEnrollDialogOpen}
          />
          <UserDetailsDialog
            user={selectedUser}
            open={detailsDialogOpen}
            onOpenChange={setDetailsDialogOpen}
          />
          <EditUserDialog
            user={selectedUser}
            open={editDialogOpen}
            onOpenChange={setEditDialogOpen}
          />
        </>
      )}
    </>
  )
}
